import React from 'react';
import Latex from 'react-latex-next';
import 'katex/dist/katex.min.css';

/**
 * Unified LaTeX sanitization and rendering utility.
 * Ensures clean, readable mathematical and textual output.
 */

export const sanitizeLatex = (content: string): string => {
  if (!content) return '';
  if (typeof content !== 'string') return String(content);

  let cleaned = content.trim();

  // 1. Remove markdown backticks
  cleaned = cleaned.replace(/```[a-z]*\n?/gi, '').replace(/```/g, '');

  // 2. Fix corrupted word splits caused by previous regexes
  cleaned = cleaned.replace(/\bIn\s+for\s+mation\b/gi, 'Information');
  cleaned = cleaned.replace(/\bIn\s+formation\b/gi, 'Information');
  cleaned = cleaned.replace(/\buni\s+form\b/gi, 'uniform');
  cleaned = cleaned.replace(/\btrans\s+for\s+mation\b/gi, 'transformation');
  cleaned = cleaned.replace(/\bthere\s+for\s+e\b/gi, 'therefore');

  // 3. Fix matrix and determinant environment names corrupted by space insertions
  cleaned = cleaned.replace(/\\begin\s*\{\s*p\s*matrix\s*\}/gi, '\\begin{pmatrix}');
  cleaned = cleaned.replace(/\\end\s*\{\s*p\s*matrix\s*\}/gi, '\\end{pmatrix}');
  cleaned = cleaned.replace(/\\begin\s*\{\s*v\s*matrix\s*\}/gi, '\\begin{vmatrix}');
  cleaned = cleaned.replace(/\\end\s*\{\s*v\s*matrix\s*\}/gi, '\\end{vmatrix}');
  cleaned = cleaned.replace(/\\begin\s*\{\s*b\s*matrix\s*\}/gi, '\\begin{bmatrix}');
  cleaned = cleaned.replace(/\\end\s*\{\s*b\s*matrix\s*\}/gi, '\\end{bmatrix}');
  cleaned = cleaned.replace(/\\begin\s*\{\s*matrix\s*\}/gi, '\\begin{matrix}');
  cleaned = cleaned.replace(/\\end\s*\{\s*matrix\s*\}/gi, '\\end{matrix}');
  cleaned = cleaned.replace(/\\begin\s*\{\s*aligned\s*\}/gi, '\\begin{aligned}');
  cleaned = cleaned.replace(/\\end\s*\{\s*aligned\s*\}/gi, '\\end{aligned}');
  cleaned = cleaned.replace(/\\begin\s*\{\s*cases\s*\}/gi, '\\begin{cases}');
  cleaned = cleaned.replace(/\\end\s*\{\s*cases\s*\}/gi, '\\end{cases}');

  // 4. Fix specific concatenated words and phrases (Never concatenate adjacent words, labels, or units)
  cleaned = cleaned.replace(/\bRefractiveindexofeyemedium\b/gi, 'Refractive index of eye medium');
  cleaned = cleaned.replace(/\bRefractiveindexofair\b/gi, 'Refractive index of air');
  cleaned = cleaned.replace(/\bRefractiveindex\b/gi, 'Refractive index');
  cleaned = cleaned.replace(/\bREFRACTIVEINDEXOFAIR\b/gi, 'REFRACTIVE INDEX OF AIR');
  cleaned = cleaned.replace(/\bREFRACTIVEINDEX\b/gi, 'REFRACTIVE INDEX');
  cleaned = cleaned.replace(/\bRadiusofcurvature\b/gi, 'Radius of curvature');
  cleaned = cleaned.replace(/\bRADIUSOFCURVATURE\b/gi, 'RADIUS OF CURVATURE');
  cleaned = cleaned.replace(/\bObjectdistance\b/gi, 'Object distance');
  cleaned = cleaned.replace(/\bOBJECTDISTANCE\b/gi, 'OBJECT DISTANCE');
  cleaned = cleaned.replace(/\bImagedistance\b/gi, 'Image distance');
  cleaned = cleaned.replace(/\bIMAGEDISTANCE\b/gi, 'IMAGE DISTANCE');
  cleaned = cleaned.replace(/\bFocallength\b/gi, 'Focal length');
  cleaned = cleaned.replace(/\bFOCALLENGTH\b/gi, 'FOCAL LENGTH');
  cleaned = cleaned.replace(/\bFinalcalculation\b/gi, 'Final calculation');
  cleaned = cleaned.replace(/\bFINALCALCULATION\b/gi, 'FINAL CALCULATION');
  cleaned = cleaned.replace(/\bParallelrays\b/gi, 'Parallel rays');
  cleaned = cleaned.replace(/\bPARALLELRAYS\b/gi, 'PARALLEL RAYS');
  cleaned = cleaned.replace(/\bGivendata\b/gi, 'Given data');
  cleaned = cleaned.replace(/\bGIVENDATA\b/gi, 'GIVEN DATA');
  cleaned = cleaned.replace(/\bConditionfornon-zero\b/gi, 'Condition for non-zero');
  cleaned = cleaned.replace(/\bparabolaequation:\b/gi, 'parabola equation:');
  cleaned = cleaned.replace(/\bellipseequation:\b/gi, 'ellipse equation:');
  cleaned = cleaned.replace(/\bhyperbolaequation:\b/gi, 'hyperbola equation:');
  cleaned = cleaned.replace(/\blinearcombination\b/gi, 'linear combination');
  cleaned = cleaned.replace(/\bhomogeneoussystem\b/gi, 'homogeneous system');
  cleaned = cleaned.replace(/\btrivialsolution\b/gi, 'trivial solution');
  cleaned = cleaned.replace(/\bindexof\b/gi, 'index of');
  cleaned = cleaned.replace(/\bofeye\b/gi, 'of eye');
  cleaned = cleaned.replace(/\beyemedium\b/gi, 'eye medium');
  cleaned = cleaned.replace(/\bofair\b/gi, 'of air');
  cleaned = cleaned.replace(/\bradiusof\b/gi, 'radius of');
  cleaned = cleaned.replace(/\bofcurvature\b/gi, 'of curvature');
  cleaned = cleaned.replace(/\bspeedof\b/gi, 'speed of');
  cleaned = cleaned.replace(/\bvelocityof\b/gi, 'velocity of');
  cleaned = cleaned.replace(/\bdistanceof\b/gi, 'distance of');
  cleaned = cleaned.replace(/\bmassof\b/gi, 'mass of');
  cleaned = cleaned.replace(/\bfocallength\b/gi, 'focal length');
  cleaned = cleaned.replace(/\bobjectdistance\b/gi, 'object distance');
  cleaned = cleaned.replace(/\bimagedistance\b/gi, 'image distance');
  cleaned = cleaned.replace(/\bsolvingfor\b/gi, 'solving for');
  cleaned = cleaned.replace(/\bequalto\b/gi, 'equal to');
  cleaned = cleaned.replace(/\bproportionalto\b/gi, 'proportional to');
  cleaned = cleaned.replace(/\bsubstitutefor\b/gi, 'substitute for');
  cleaned = cleaned.replace(/\bconditionfor\b/gi, 'condition for');
  cleaned = cleaned.replace(/\bgivendata\b/gi, 'given data');
  cleaned = cleaned.replace(/\bfinalcalculation\b/gi, 'final calculation');
  cleaned = cleaned.replace(/\bexamplecalculation\b/gi, 'example calculation');
  cleaned = cleaned.replace(/\bstepbystep\b/gi, 'step by step');
  cleaned = cleaned.replace(/\bparallelrays\b/gi, 'parallel rays');
  cleaned = cleaned.replace(/\bdirectionof\b/gi, 'direction of');
  cleaned = cleaned.replace(/\bmagnitudeof\b/gi, 'magnitude of');
  cleaned = cleaned.replace(/\brateofchange\b/gi, 'rate of change');

  // Fix PascalCase/camelCase joined words in text phrases (e.g. "RefractiveIndex" -> "Refractive Index", "EyeMedium" -> "Eye Medium")
  cleaned = cleaned.replace(/\b([A-Z][a-z]{2,})([A-Z][a-z]{2,})\b/g, (match, p1, p2) => {
    // Exclude standard LaTeX and code terms
    if (/^(Matrix|Pmatrix|Bmatrix|Vmatrix|Aligned|Cases|Array|Table)$/i.test(match)) return match;
    return `${p1} ${p2}`;
  });

  // 5. Ensure normal spacing around mathematical expressions, numbers, and physical units
  cleaned = cleaned.replace(/([0-9])\s*(mm|cm|km|km\/h|m\/s\^2|m\/s|kg|g|mg|sec|seconds|Hz|kHz|MHz|kN|kJ|kW|kPa|mV|mA|μF|rad|deg|degrees|units)\b/g, '$1 $2');

  // 6. Subscript normalization in variable definitions (e.g. "(n1)" -> "(n_1)", "(n2)" -> "(n_2)", "(N1)" -> "(N_1)")
  cleaned = cleaned.replace(/\(([a-zA-Z])(\d+)\)/g, '($1_$2)');

  // 7. Ensure spacing between words, parentheses and equals signs (e.g. "medium(n_2)" -> "medium (n_2)", "(n_2)=" -> "(n_2) = ")
  cleaned = cleaned.replace(/([a-zA-Z])\((\s*[a-zA-Z0-9_\^\\\{\}]+\s*)\)/g, '$1 ($2)');
  cleaned = cleaned.replace(/\)\s*=\s*/g, ') = ');

  // 8. Clean up bracket spacing and spacing tags
  cleaned = cleaned.replace(/\\+\[\s*\d*\.?\d*\s*e\s*m\s*\]/g, ' ');

  // 9. Clean isolated trailing backslashes
  cleaned = cleaned.replace(/\\$/gm, '');

  return cleaned.trim();
};

export function isEnglishLanguage(lang?: string): boolean {
  if (!lang) return true;
  const l = lang.toLowerCase().trim();
  return l === 'english' || l === 'en' || l.startsWith('en-');
}

/**
 * Strips raw LaTeX math markup, dollar signs, unrendered commands, and control artifacts
 * to produce clean, legible, human-readable caption text in the selected language.
 */
export function cleanCaptionText(text: string, language?: string): string {
  if (!text) return "";
  let s = String(text).trim();

  // Remove markdown fences, line break markers, and control characters
  s = s.replace(/```[a-z]*\n?/gi, '').replace(/```/g, '');
  s = s.replace(/\[BR\]/gi, ' ');
  s = s.replace(/\\b(?![a-zA-Z])/g, '').replace(/[\b]/g, '');
  s = s.replace(/\b(null|undefined|\[object Object\]|NaN|Infinity)\b/gi, '');

  // Degrees handling
  s = s.replace(/(\d+)\s*\^?\s*\\?circ\b/g, '$1°');
  s = s.replace(/(\d+)\s*\^\{\\?circ\}/g, '$1°');
  s = s.replace(/\bto the power of \\?circ\b/gi, '°');
  s = s.replace(/\bto the power of\b/gi, '^');

  // Fractions
  s = s.replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, '$1/$2');

  // Roots
  s = s.replace(/\\sqrt\[?.*?\]?\{([^}]+)\}/g, '√$1');

  // Trig & math functions
  s = s.replace(/\\(cos|sin|tan|sec|csc|cosec|cot|log|ln|lim|exp)\b/g, '$1');

  // Greek letters
  s = s.replace(/\\Delta/g, 'Δ');
  s = s.replace(/\\theta/g, 'θ');
  s = s.replace(/\\pi/g, 'π');
  s = s.replace(/\\alpha/g, 'α');
  s = s.replace(/\\beta/g, 'β');
  s = s.replace(/\\gamma/g, 'γ');
  s = s.replace(/\\lambda/g, 'λ');
  s = s.replace(/\\omega/g, 'ω');
  s = s.replace(/\\sigma/g, 'σ');
  s = s.replace(/\\phi/g, 'φ');

  // Symbols
  s = s.replace(/\\times/g, '×');
  s = s.replace(/\\div/g, '÷');
  s = s.replace(/\\pm/g, '±');
  s = s.replace(/\\leq?/g, '≤');
  s = s.replace(/\\geq?/g, '≥');
  s = s.replace(/\\neq/g, '≠');
  s = s.replace(/\\approx/g, '≈');
  s = s.replace(/\\cdot/g, '·');
  s = s.replace(/\\text\{([^}]+)\}/g, '$1');
  s = s.replace(/\\mathbf\{([^}]+)\}/g, '$1');
  s = s.replace(/\\mathrm\{([^}]+)\}/g, '$1');
  s = s.replace(/\\vec\{([^}]+)\}/g, '$1');

  // Strip all delimiters, dollar signs, braces, and remaining backslashes
  s = s.replace(/\$\$/g, '');
  s = s.replace(/\$/g, '');
  s = s.replace(/\\[a-zA-Z]+/g, ' ');
  s = s.replace(/[\{\}\\]/g, '');
  s = s.replace(/[*_~`#]/g, '');

  // Enforce English brand name "Professor X" across all Indic scripts
  s = s.replace(/प्रोफेसर\s*(एक्स|X|x)/gi, 'Professor X');
  s = s.replace(/प्राध्यापक\s*(एक्स|X|x)/gi, 'Professor X');

  return s.replace(/\s{2,}/g, ' ').trim();
}

/**
 * Normalizes text for TTS speech engines.
 * Separates spoken narration from visual LaTeX math.
 * Language-aware: preserves native numbers & symbols for Indian/foreign languages
 * without injecting English words ("divided by", "to the power of", "meters per second").
 */
export function cleanForSpeech(text: string, language?: string): string {
  if (!text) return "";
  const isEng = isEnglishLanguage(language);
  let s = String(text).trim();

  // Remove markdown fences or control tokens
  s = s.replace(/\[BR\]/gi, ' ');
  s = s.replace(/\\b(?![a-zA-Z])/g, '').replace(/[\b]/g, '');
  s = s.replace(/\b(null|undefined|\[object Object\]|NaN|Infinity)\b/gi, '');

  // Handle degrees
  if (isEng) {
    s = s.replace(/(\d+)\s*\^?\s*\\?circ\b/g, '$1 degrees');
    s = s.replace(/(\d+)\s*\^\{\\?circ\}/g, '$1 degrees');
    s = s.replace(/(\d+)\s*°/g, '$1 degrees');
  } else {
    s = s.replace(/(\d+)\s*\^?\s*\\?circ\b/g, '$1 degree');
    s = s.replace(/(\d+)\s*\^\{\\?circ\}/g, '$1 degree');
    s = s.replace(/(\d+)\s*°/g, '$1 degree');
  }
  s = s.replace(/\bto the power of \\?circ\b/gi, isEng ? 'degrees' : 'degree');

  // Fractions
  if (isEng) {
    s = s.replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, '$1 over $2');
  } else {
    s = s.replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, '$1/$2');
  }

  // Roots
  s = s.replace(/\\sqrt\[?.*?\]?\{([^}]+)\}/g, isEng ? 'square root of $1' : 'root $1');

  // Trig functions
  s = s.replace(/\\(cos|sin|tan|sec|csc|cosec|cot|log|ln|lim|exp)\b/g, '$1');

  // Greek letters phonetics
  s = s.replace(/\\Delta/g, 'delta');
  s = s.replace(/\\theta/g, 'theta');
  s = s.replace(/\\pi/g, 'pi');
  s = s.replace(/\\alpha/g, 'alpha');
  s = s.replace(/\\beta/g, 'beta');
  s = s.replace(/\\gamma/g, 'gamma');
  s = s.replace(/\\lambda/g, 'lambda');
  s = s.replace(/\\omega/g, 'omega');
  s = s.replace(/\\sigma/g, 'sigma');
  s = s.replace(/\\phi/g, 'phi');

  // Units & operators ONLY if English
  if (isEng) {
    s = s.replace(/(\d+([.,]\d+)?)\s?m\/s\^2/g, '$1 meters per second squared');
    s = s.replace(/(\d+([.,]\d+)?)\s?m\/s/g, '$1 meters per second');
    s = s.replace(/(\d+([.,]\d+)?)\s?kg/g, '$1 kilograms');
    s = s.replace(/(\d+([.,]\d+)?)\s?cm/g, '$1 centimeters');
    s = s.replace(/(\d+([.,]\d+)?)\s?mm/g, '$1 millimeters');
    s = s.replace(/(\d+([.,]\d+)?)\s?km\/h/g, '$1 kilometers per hour');
    s = s.replace(/(\d+([.,]\d+)?)\s?km/g, '$1 kilometers');
    s = s.replace(/(\d+([.,]\d+)?)\s?m\b/g, '$1 meters');
    s = s.replace(/(\d+([.,]\d+)?)\s?s\b/g, '$1 seconds');
    s = s.replace(/(\d+([.,]\d+)?)\s?Hz/g, '$1 Hertz');
    s = s.replace(/(\d+([.,]\d+)?)\s?N\b/g, '$1 Newtons');
    s = s.replace(/(\d+([.,]\d+)?)\s?% ?/g, '$1 percent ');
    s = s.replace(/(\d+)\s?=\s?/g, '$1 equals ');
    s = s.replace(/(\d+)\s?\+\s?/g, '$1 plus ');
    s = s.replace(/([0-9])\s?-\s?([0-9])/g, '$1 minus $2');
    s = s.replace(/(\d+)\s?\*\s?/g, '$1 times ');
    s = s.replace(/(\d+)\s?\/\s?(\d+)/g, '$1 divided by $2');
    s = s.replace(/(\d+([.,]\d+)?)[eE]([+-]?\d+)/g, '$1 times 10 to the power of $3');
    s = s.replace(/\b(\d+)\^(\d+)\b/g, '$1 to the power of $2');
    s = s.replace(/\^/g, ' to the power of ');
  } else {
    // Non-English: preserve numbers and native symbols; do not inject English words
    s = s.replace(/\^/g, ' ');
    s = s.replace(/\\times/g, '×');
    s = s.replace(/\\div/g, '÷');
    s = s.replace(/\\pm/g, '±');
    s = s.replace(/\\leq?/g, '≤');
    s = s.replace(/\\geq?/g, '≥');
    s = s.replace(/\\neq/g, '≠');
  }

  // Common LaTeX cleanup
  s = s.replace(/\\text\{([^}]+)\}/g, '$1');
  s = s.replace(/\\mathbf\{([^}]+)\}/g, '$1');
  s = s.replace(/\\mathrm\{([^}]+)\}/g, '$1');
  s = s.replace(/\\vec\{([^}]+)\}/g, '$1');

  // Strip all delimiters, dollar signs, braces, and remaining backslashes
  s = s.replace(/\$\$/g, '');
  s = s.replace(/\$/g, '');
  s = s.replace(/\\[a-zA-Z]+/g, ' ');
  s = s.replace(/[\{\}\\]/g, ' ');
  s = s.replace(/[*_~`#]/g, ' ');

  // Enforce English brand name "Professor X" across all Indic scripts and translations
  s = s.replace(/प्रोफेसर\s*(एक्स|X|x)/gi, 'Professor X');
  s = s.replace(/प्राध्यापक\s*(एक्स|X|x)/gi, 'Professor X');
  s = s.replace(/प्रोफेसर/gi, 'Professor X');
  s = s.replace(/प्राध्यापक/gi, 'Professor X');

  return s.replace(/\s{2,}/g, ' ').trim();
}

/**
 * Detects whether a string contains multi-letter natural language words
 * indicating English labels/phrases that must NOT be rendered in math mode.
 */
export function hasNaturalLanguageWords(str: string): boolean {
  if (!str) return false;
  // Remove LaTeX environment tags and commands
  const stripped = str
    .replace(/\\begin\{[^}]+\}/g, ' ')
    .replace(/\\end\{[^}]+\}/g, ' ')
    .replace(/\\text\{[^}]+\}/g, ' ')
    .replace(/\\[a-zA-Z]+/g, ' ')
    .replace(/[^a-zA-Z\s]/g, ' ');

  // Standard math functions and differential tokens
  const mathFunctions = new Set([
    'sin', 'cos', 'tan', 'sec', 'csc', 'cot', 'log', 'ln', 'exp', 'lim', 'det',
    'max', 'min', 'sup', 'inf', 'arg', 'dim', 'ker', 'mod', 'deg', 'gcd', 'lcm',
    'dx', 'dy', 'dz', 'dt', 'dr', 'ds', 'du', 'dv', 'dw'
  ]);

  const words = stripped
    .split(/\s+/)
    .map(w => w.trim().toLowerCase())
    .filter(w => w.length >= 2 && !mathFunctions.has(w));

  // If 2 or more normal words, or 1 word of length >= 4 (e.g. "Given", "Refractive", "Radius", "Final")
  if (words.length >= 2) return true;
  if (words.length === 1 && words[0].length >= 4) return true;
  return false;
}

/**
 * Formats a value or expression for KaTeX, ensuring numbers with units use upright text.
 */
function formatMathValueWithUnit(val: string): string {
  const trimmed = val.trim();
  const unitRegex = /^([+-]?\d+([.,]\d+)?)\s*(mm|cm|km|km\/h|m\/s\^2|m\/s|kg|g|mg|sec|seconds|Hz|kHz|MHz|kN|kJ|kW|kPa|mV|mA|μF|rad|deg|degrees|units)?$/i;
  const match = trimmed.match(unitRegex);
  if (match) {
    const num = match[1];
    const unit = match[3];
    return unit ? `${num}\\text{ ${unit}}` : num;
  }
  return trimmed;
}

export const formatLatexString = (content: string, inline: boolean = false): string => {
  if (!content) return '';
  let trimmed = sanitizeLatex(content);

  // If string contains natural language words, it should NEVER be wrapped in outer $ or $$ math mode
  const hasWords = hasNaturalLanguageWords(trimmed);

  // If wrapped in outer $ ... $ or $$ ... $$:
  if ((trimmed.startsWith('$$') && trimmed.endsWith('$$')) || (trimmed.startsWith('$') && trimmed.endsWith('$'))) {
    if (hasWords) {
      // Strip accidental outer $ delimiters that cause word joining
      trimmed = trimmed.replace(/^\$\$|\$\$$|^[$]|[$]$/g, '').trim();
    } else {
      return trimmed;
    }
  }

  // If contains LaTeX environment (matrix, determinant, aligned, cases)
  if (trimmed.includes('\\begin{')) {
    if (!trimmed.includes('$') && !trimmed.includes('\\[') && !trimmed.includes('\\(')) {
      return `$$${trimmed}$$`;
    }
    return trimmed;
  }

  // If text already has standard delimiters, preserve them safely
  if (trimmed.includes('$') || trimmed.includes('\\[') || trimmed.includes('\\(')) {
    return trimmed;
  }

  // If the string is natural language text or mixed text + equations:
  if (hasWords) {
    // Pattern 1: Heading / Label with colon followed by formula or value:
    // e.g. "GIVEN DATA : REFRACTIVE INDEX OF AIR (N1) = 1.0" or "Formula: v = u + at"
    if (trimmed.includes(':')) {
      const colonIdx = trimmed.indexOf(':');
      const labelPart = trimmed.slice(0, colonIdx).trim();
      const restPart = trimmed.slice(colonIdx + 1).trim();

      if (!restPart) {
        return `${labelPart}:`;
      }

      // Format the rest part appropriately
      return `${labelPart}: ${formatLatexString(restPart, true)}`;
    }

    // Pattern 2: "Text Label (Symbol) = Value [Unit] [(Optional Note)]"
    // e.g. "Refractive index of eye medium (n_2) = 1.34" or "Radius of curvature (R) = +7.8 mm"
    // or "Object distance (u) = \infty (Parallel rays)"
    const labelSymValMatch = trimmed.match(/^([^=]+?)\s*(\([a-zA-Z0-9_\\\^\{\}\s]+\))\s*=\s*(.+)$/);
    if (labelSymValMatch) {
      const labelText = labelSymValMatch[1].trim();
      const symbol = labelSymValMatch[2].trim();
      const rawVal = labelSymValMatch[3].trim();

      // Check for trailing parenthetical note (e.g. "(Parallel rays)" or "(given)")
      const noteMatch = rawVal.match(/^([^(]+?)\s*(\([^)]+\))$/);
      if (noteMatch) {
        const mathVal = formatMathValueWithUnit(noteMatch[1].trim());
        const noteText = noteMatch[2].trim();
        return `${labelText} $${symbol} = ${mathVal}$ ${noteText}`;
      }

      const val = formatMathValueWithUnit(rawVal);
      return `${labelText} $${symbol} = ${val}$`;
    }

    // Pattern 3: "Text Label = Value [Unit] [(Optional Note)]"
    // e.g. "Focal length = 15 cm" or "Velocity = 340 m/s"
    const labelValMatch = trimmed.match(/^([a-zA-Z\s]{3,})\s*=\s*(.+)$/);
    if (labelValMatch) {
      const labelText = labelValMatch[1].trim();
      const rawVal = labelValMatch[2].trim();

      const noteMatch = rawVal.match(/^([^(]+?)\s*(\([^)]+\))$/);
      if (noteMatch) {
        const mathVal = formatMathValueWithUnit(noteMatch[1].trim());
        const noteText = noteMatch[2].trim();
        return `${labelText} $= ${mathVal}$ ${noteText}`;
      }

      const val = formatMathValueWithUnit(rawVal);
      return `${labelText} $= ${val}$`;
    }

    // Pattern 4: Text with inline LaTeX math commands (e.g. \frac, \sqrt, \lambda, \theta)
    if (/\\[a-zA-Z]+|[\^_\{\}]/.test(trimmed)) {
      // Wrap isolated math expressions into $...$ while preserving English text
      const tokenized = trimmed.replace(/(\\[a-zA-Z]+(\{[^}]*\})*|[a-zA-Z0-9_]+\s*=\s*[^,\s]+)/g, (match) => {
        if (/^(Information|Refractive|Radius|Given|Step|Example|Calculation)$/i.test(match)) return match;
        return `$${match}$`;
      });
      return tokenized;
    }

    // Plain text or heading without mathematical operators
    return trimmed;
  }

  // Pure mathematical equation without natural language words (e.g. \frac{1.34}{v} = \frac{0.34}{7.8})
  const hasLatexCommands = /\\[a-zA-Z]+|[\^_\{\}]/.test(trimmed);
  const isPureEquation = /^[a-zA-Z0-9_\^\{\}\\\+\-\*\/\=\(\)\,\.\s\|\<\>\:\;\-]+$/.test(trimmed) && 
    (trimmed.includes('=') || trimmed.includes('\\') || trimmed.includes('|') || trimmed.includes('^') || trimmed.includes('_'));

  if (hasLatexCommands || isPureEquation) {
    const isBlock = !inline && (
      trimmed.includes('\\frac') || 
      trimmed.includes('\\sqrt') || 
      trimmed.includes('\\int') || 
      trimmed.includes('\\sum') ||
      trimmed.includes('\\prod') ||
      trimmed.includes('\\begin')
    );
    return isBlock ? `$$${trimmed}$$` : `$${trimmed}$`;
  }

  return trimmed;
};

export const MathRenderer: React.FC<{ content: string; inline?: boolean; className?: string }> = ({ content, inline = false, className = "" }) => {
  const formatted = formatLatexString(content, inline);
  
  if (!formatted) return null;

  return (
    <div className={`whitespace-normal break-words text-left leading-relaxed ${className}`} style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}>
      <Latex strict={false}>
        {formatted}
      </Latex>
    </div>
  );
};
